import { useCallback, useMemo } from 'react';
import { useHourOverageCalculation } from './use-hour-overage-calculation';
import { HourOverageOverride } from './use-hour-overage-calculation';
import { InventoryReturnRequest, Order } from '../../../../../clients/quipli-service/order/order';

export const useHourOverageManagement = (
  inventoryReturnRequest?: InventoryReturnRequest,
  order?: Order,
  checkCanInventoryReturn = false,
  debounceTime = 500,
) => {
  // Use the existing hook from inventory-return
  const { canInventoryReturn, hourOverages, hourOverageOverrides, setHourOverageOverrides, isCalculating } =
    useHourOverageCalculation(inventoryReturnRequest, order, checkCanInventoryReturn, debounceTime);

  // Helper function to update a specific overage override
  const updateOverageOverride = useCallback(
    (orderProductId: number, unitId: number, override: HourOverageOverride) => {
      setHourOverageOverrides((existing) => [
        ...existing.filter((ov) => !(ov.orderProductId === orderProductId && ov.unitId === unitId)),
        override,
      ]);
    },
    [setHourOverageOverrides],
  );

  // Helper function to get overage for a specific unit
  const getOverageForUnit = useCallback(
    (orderProductId: number, unitId: number) => {
      return hourOverages.find((overage) => overage.orderProductId === orderProductId && overage.unitId === unitId);
    },
    [hourOverages],
  );

  // Helper function to get override for a specific unit
  const getOverrideForUnit = useCallback(
    (orderProductId: number, unitId: number) => {
      return hourOverageOverrides.find(
        (override) => override.orderProductId === orderProductId && override.unitId === unitId,
      );
    },
    [hourOverageOverrides],
  );

  // Helper function to clear all overrides
  const clearAllOverrides = useCallback(() => {
    setHourOverageOverrides([]);
  }, [setHourOverageOverrides]);

  // Helper function to check if there are any overages
  const hasAnyOverage = useMemo(() => {
    return hourOverages.some((overage) => overage.overageCharge > 0);
  }, [hourOverages]);

  // Helper function to check if there are any overrides
  const hasAnyOverride = useMemo(() => {
    return hourOverageOverrides.some((override) => override.overageCharge > 0);
  }, [hourOverageOverrides]);

  return {
    // Core data from the existing hook
    canInventoryReturn,
    hourOverages,
    hourOverageOverrides,
    setHourOverageOverrides,
    isCalculating,

    // Additional helper functions
    updateOverageOverride,
    getOverageForUnit,
    getOverrideForUnit,
    clearAllOverrides,
    hasAnyOverage,
    hasAnyOverride,
  };
};
